// MeetCallWidget — picture-in-picture mini widget mounted at the intranet
// app level (not the page level) so it persists across route navigation.
// Visible whenever a LiveKit Room is connected AND the user is NOT currently
// on the /meet call view. Lets the user return to the call or leave from
// anywhere in the intranet.
//
// The LiveKit Room itself is a module variable in connectCall.js, so the
// call stays connected even when the user navigates away from /meet.
// This widget just renders a live view of that persistent Room.

import { getLivekitRoom } from '../../functions/meet/connectCall.js'
import { routeTo } from '../../functions/meet/meetUtils.js'

// Attach-once helper — same idempotent pattern as ParticipantTile.js.
const _attachLocalVideo = (videoEl) => {
  if (!videoEl) return
  const room = getLivekitRoom()
  if (!room?.localParticipant) return
  const pubs = typeof room.localParticipant.videoTrackPublications?.values === 'function'
    ? [...room.localParticipant.videoTrackPublications.values()]
    : Object.values(room.localParticipant.videoTrackPublications || {})
  let targetPub = null
  for (const pub of pubs) {
    if (pub.track && (pub.source === 'camera' || pub.kind === 'video')) {
      targetPub = pub
      break
    }
  }
  const currentSid = videoEl._lkTrackSid || null
  const targetSid = targetPub?.trackSid || null
  if (currentSid === targetSid) return
  if (videoEl._lkTrack) {
    try { videoEl._lkTrack.detach(videoEl) } catch { /* ignore */ }
  }
  if (targetPub?.track) {
    try {
      targetPub.track.attach(videoEl)
      videoEl._lkTrack = targetPub.track
      videoEl._lkTrackSid = targetSid
    } catch (err) { console.warn('[widget] attach failed', err) }
  }
}

export const MeetCallWidget = {
  // Hide unless there's an active LiveKit connection AND we're off the call view.
  hide: (el, s) => {
    if (!s.root?.livekitConnected) return true
    if (s.root?.meetView === 'call') return true
    return false
  },

  position: 'fixed',
  bottom: 'B',
  right: 'B',
  width: 'H',
  height: 'G1',
  zIndex: '1500',
  flow: 'y',
  round: 'A',
  overflow: 'hidden',
  background: 'black',
  borderWidth: '2px',
  borderColor: 'cyan',
  borderStyle: 'solid',
  // A subtle glow to signal "call in progress".
  shadow: 'D1 cyan',

  onRender: (el) => {
    // Re-attach the local video track on every render (idempotent).
    const node = el.node
    if (!node) return
    const videoEl = node.querySelector('video')
    _attachLocalVideo(videoEl)
  },

  VideoTile: {
    position: 'relative',
    flex: '1 1 0',
    minHeight: '0',
    align: 'center center',

    VideoEl: {
      tag: 'video',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      attr: {
        autoplay: 'autoplay',
        playsinline: 'playsinline',
        muted: 'muted'
      }
    },

    LiveBadge: {
      position: 'absolute',
      top: 'V',
      left: 'V',
      padding: 'V Z',
      round: 'Y',
      background: 'red',
      color: 'white',
      fontSize: 'Y1',
      fontWeight: '700',
      letterSpacing: '.05em',
      textTransform: 'uppercase',
      text: '● Live'
    }
  },

  Footer: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    padding: 'V Z',
    background: 'overlayStrong',

    RoomName: {
      tag: 'span',
      flex: '1',
      fontSize: 'Y2',
      color: 'title',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        const room = (s.root?.rooms || []).find(r => r.id === s.root?.activeRoomId)
        return room?.name || 'Call'
      }
    },

    ReturnBtn: {
      tag: 'button',
      text: '↗',
      title: 'Return to call',
      padding: 'V Z',
      round: 'Z',
      border: 'none',
      background: 'cyan',
      color: 'black',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '700',
      onClick: (e, el, s) => {
        const id = s.root?.activeRoomId
        if (!id) return
        s.root?.update({ meetView: 'call' })
        if (typeof window !== 'undefined') {
          history.replaceState(null, '', `#room=${id}&view=call`)
        }
        routeTo(el, '/meet')
      }
    },

    LeaveBtn: {
      tag: 'button',
      text: '×',
      title: 'Leave call',
      padding: 'V Z',
      round: 'Z',
      border: 'none',
      background: 'red',
      color: 'white',
      cursor: 'pointer',
      fontSize: 'Z2',
      fontWeight: '700',
      onClick: async (e, el, s) => {
        await el.call('disconnectCall')
        s.root?.update({ meetView: 'list' })
      }
    }
  }
}
